import { ApplicationInsights, ITelemetryPlugin } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';

// React plugin for Application Insights
// Provides automatic route change tracking and component error boundary integration
export const reactPlugin = new ReactPlugin();

// Application Insights instance
// Initialized lazily - only when connection string is provided via initializeTelemetry()
let appInsights: ApplicationInsights | null = null;
let isInitialized = false;

/**
 * Initializes Application Insights telemetry.
 * Only initializes if a valid connection string is provided.
 * Safe to call multiple times - subsequent calls are no-ops.
 *
 * @param connectionString - Azure Application Insights connection string from backend config
 */
export function initializeTelemetry(connectionString: string | undefined | null): void {
  // Skip initialization if no connection string (local development)
  if (!connectionString) {
    if (!isInitialized) {
      console.info('[Telemetry] No connection string provided - telemetry disabled');
      isInitialized = true;
    }
    return;
  }

  // Prevent double initialization
  if (appInsights) {
    console.debug('[Telemetry] Already initialized, skipping');
    return;
  }

  console.info('[Telemetry] Initializing with connection string...');

  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      extensions: [reactPlugin as unknown as ITelemetryPlugin],
      // Automatic tracking
      enableAutoRouteTracking: true,
      // Cross-origin correlation with backend
      enableCorsCorrelation: true,
      correlationHeaderExcludedDomains: ['*.google.com', '*.googleapis.com'],
      // Request/response header tracking for debugging
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      // Disable deprecated features
      disableFetchTracking: false,
      disableAjaxTracking: false,
      // Performance settings
      maxBatchInterval: 5000, // Send telemetry every 5 seconds (faster for dev)
      disableExceptionTracking: false,
      // Enable debugging in dev
      loggingLevelConsole: process.env.NODE_ENV === 'development' ? 2 : 0,
      // Use fetch API instead of sendBeacon (better CORS handling)
      isBeaconApiDisabled: true,
      // Disable storage buffer to avoid localStorage issues
      enableSessionStorageBuffer: false,
    },
  });

  appInsights.loadAppInsights();

  // Add custom properties to all telemetry
  appInsights.addTelemetryInitializer((envelope) => {
    if (envelope.tags) {
      envelope.tags['ai.cloud.role'] = 'mcm.client';
    }
    return true;
  });

  // Suppress CORS errors in console (browser privacy features may block telemetry)
  appInsights.addTelemetryInitializer(() => {
    // This runs before each telemetry item is sent
    // Returning true allows the item to be sent
    return true;
  });

  // Flush telemetry before page unload to avoid data loss
  window.addEventListener('beforeunload', () => {
    void appInsights?.flush();
  });

  isInitialized = true;
  console.info('[Telemetry] Application Insights initialized successfully');
  console.info('[Telemetry] Note: Browser privacy features may block client telemetry. Backend telemetry is unaffected.');

  // Track initial page view
  appInsights.trackPageView({ name: window.location.pathname });
}

/**
 * Tracks a custom event.
 * @param name - Event name
 * @param properties - Optional custom properties
 */
export function trackEvent(name: string, properties?: Record<string, string>): void {
  if (!appInsights) {
    if (import.meta.env.DEV) {
      console.warn('[Telemetry] trackEvent called but telemetry not initialized:', name);
    }
    return;
  }
  console.debug('[Telemetry] Tracking event:', name, properties);
  appInsights.trackEvent({ name }, properties);
}

/**
 * Tracks an exception.
 * @param error - The error to track
 * @param properties - Optional custom properties
 */
export function trackException(error: Error, properties?: Record<string, string>): void {
  if (!appInsights) {
    if (import.meta.env.DEV) {
      console.warn('[Telemetry] trackException called but telemetry not initialized:', error.message);
    }
    return;
  }
  console.debug('[Telemetry] Tracking exception:', error.message, properties);
  appInsights.trackException({ exception: error }, properties);
}

/**
 * Tracks a page view manually.
 * Usually not needed since enableAutoRouteTracking is enabled.
 * @param name - Page name
 */
export function trackPageView(name: string): void {
  if (!appInsights) {
    if (import.meta.env.DEV) {
      console.warn('[Telemetry] trackPageView called but telemetry not initialized:', name);
    }
    return;
  }
  console.debug('[Telemetry] Tracking page view:', name);
  appInsights.trackPageView({ name });
}

/**
 * Flushes all pending telemetry to the server.
 * Call this before closing/navigating away from the page.
 */
export function flushTelemetry(): void {
  if (appInsights) {
    console.debug('[Telemetry] Flushing telemetry...');
    void appInsights.flush();
  }
}

/**
 * Returns whether telemetry is currently initialized.
 */
export function isTelemetryInitialized(): boolean {
  return appInsights !== null;
}

export { appInsights };
